// mcpApprovalsApi — dashboard client for the governed MCP tool-call approval
// inbox. Talks to runtime-api's control-plane endpoints:
//   GET  /v1/runtime/mcp-tool-call-approvals?status=pending&limit=
//   POST /v1/runtime/mcp-tool-call-approvals/:id/decide  { verdict }
//
// Spec: docs/specs/nexus-mcp-approval-audit-v0.md (human-approval + audit).
// Backend: apps/runtime-api/src/index.ts (mcpApprovalInboxView + /decide).
//
// The inbox view is already redacted server-side: it carries ONLY the tool
// name, the requester identity (user id + role), an argument *summary*
// (arg_keys + arg_count — NEVER raw values), and the timing (created_at /
// deadline). The opaque correlation_token / secrets / raw payload never
// cross the wire here.
import { apiFetch } from "../../lib/api-client.js";

/** One pending gated MCP tool-call, as surfaced to a human approver.
 *  Mirror of runtime-api `mcpApprovalInboxView` return shape. */
export interface McpApprovalRow {
  id: string;
  tool_name: string;
  /** Identity that requested the gated call. The four-eyes rule means the
   *  approver must NOT be this same identity (server enforces; 403 otherwise). */
  actor_user_id: string;
  actor_role: string;
  /** Argument key names only — the redacted summary. NEVER the values. */
  arg_keys: string[];
  arg_count: number;
  created_at: string;
  /** ISO deadline after which the cron sweep fail-closes the row to timed_out. */
  deadline: string | null;
}

export interface McpApprovalListResponse {
  approvals: McpApprovalRow[];
  tenant_id: string;
  correlation_id: string | null;
}

export type McpVerdict = "approved" | "rejected";

/** /decide response. On approve, `executed` reflects whether the actor-replay
 *  execution actually ran; `executed:false` + `reason` means the decision
 *  landed but the run did not (fail-closed, surfaced — never fabricated). */
export interface McpDecideResponse {
  status: "approved" | "rejected";
  approval_id: string;
  executed: boolean;
  reason?: string | null;
  tenant_id: string;
  correlation_id: string | null;
}

export class McpApprovalApiError extends Error {
  status: number;
  code: string;
  correlationId: string | null;
  upstreamMessage?: string;
  constructor(status: number, code: string, cid: string | null, msg?: string) {
    super(code);
    this.status = status;
    this.code = code;
    this.correlationId = cid;
    this.upstreamMessage = msg;
  }
}

export function friendlyMcpApprovalError(err: unknown): string {
  if (err instanceof McpApprovalApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403 && err.code === "four_eyes_self_approval")
      return "您不能核准自己提出的呼叫。為了雙人覆核，核准者必須是另一個人。";
    if (err.status === 403) return "您目前的角色不能核准 MCP 工具呼叫（需要「核准 MCP 工具呼叫」權限）。";
    if (err.status === 404 && err.code === "approval_not_found")
      return "找不到這筆核准請求（可能已被處理或逾時）。";
    if (err.status === 404 && err.code === "tenant_not_routed") return "此客戶單位尚未配置資料分片。";
    if (err.status === 404) return "找不到這筆核准請求。";
    if (err.status === 409 && err.code === "already_decided") return "這筆呼叫已經被處理過了（重新整理看最新狀態）。";
    if (err.status === 400 && err.code === "verdict_must_be_approved_or_rejected")
      return "決議內容不正確，請重試。";
    if (err.status === 400) return `請求格式不正確（${err.code}）。`;
    return `核准服務暫時無法存取（HTTP ${err.status}・code=${err.code}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<McpApprovalApiError> {
  let code = "request_failed";
  let cid: string | null = r.headers.get("x-correlation-id");
  let msg: string | undefined;
  try {
    const b = (await r.json()) as { error?: string; correlation_id?: string; message?: string };
    code = b.error ?? code;
    cid = b.correlation_id ?? cid;
    msg = b.message;
  } catch {
    /* non-JSON error body — keep defaults */
  }
  return new McpApprovalApiError(r.status, code, cid, msg);
}

export async function listMcpApprovals(
  params: { tenantId?: string | null; limit?: number } = {},
): Promise<McpApprovalListResponse> {
  const qs = new URLSearchParams();
  qs.set("status", "pending");
  if (params.tenantId) qs.set("tenant_id", params.tenantId);
  if (params.limit) qs.set("limit", String(params.limit));
  const r = await apiFetch(`/v1/runtime/mcp-tool-call-approvals?${qs}`);
  if (!r.ok) throw await asError(r);
  return (await r.json()) as McpApprovalListResponse;
}

export async function decideMcpApproval(
  id: string,
  verdict: McpVerdict,
  tenantId?: string | null,
): Promise<McpDecideResponse> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/runtime/mcp-tool-call-approvals/${encodeURIComponent(id)}/decide${qs}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ verdict }),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as McpDecideResponse;
}
